import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import clsx from 'clsx';

import { Spinner } from './Spinner';
import { VisuallyHidden } from './VisuallyHidden';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading glyph. Pair with `iconOnly` to hide the label visually. */
  icon?: ReactNode;
  /** Trailing glyph — a disclosure caret, never a decoration. */
  iconTrailing?: ReactNode;
  /**
   * Render the icon alone. `children` still ships as the accessible name, so an
   * icon-only button cannot be built without one — that is the point of making
   * `children` required rather than accepting a loose `aria-label`.
   */
  iconOnly?: boolean;
  /**
   * Busy. Stays focusable and keeps its label (a control that vanishes from the
   * tab order mid-interaction loses the user's place); clicks are swallowed and
   * `aria-disabled` + `aria-busy` carry the state.
   */
  loading?: boolean;
  fullWidth?: boolean;
  /**
   * Set on a black band or a solid classification fill: switches the focus ring
   * to the inverse contract (white ring + 1px inner ring).
   */
  onInverse?: boolean;
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    iconTrailing,
    iconOnly = false,
    loading = false,
    fullWidth = false,
    onInverse = false,
    className,
    disabled,
    type = 'button',
    onClick,
    children,
    ...rest
  },
  ref,
) {
  const inert = disabled === true || loading;

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      aria-disabled={inert ? true : undefined}
      aria-busy={loading ? true : undefined}
      data-focus-scope={onInverse ? 'inverse' : undefined}
      data-variant={variant}
      className={clsx(
        styles.button,
        styles[variant],
        styles[size],
        iconOnly && styles.iconOnly,
        fullWidth && styles.fullWidth,
        className,
      )}
      onClick={handleClick}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} label={null} className={styles.glyph} />
      ) : (
        icon !== undefined && (
          <span className={styles.glyph} aria-hidden="true">
            {icon}
          </span>
        )
      )}
      {iconOnly ? (
        <VisuallyHidden>{children}</VisuallyHidden>
      ) : (
        <span className={styles.label}>{children}</span>
      )}
      {!iconOnly && iconTrailing !== undefined && (
        <span className={styles.glyph} aria-hidden="true">
          {iconTrailing}
        </span>
      )}
    </button>
  );
});
